import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import {useAuth} from '@/hooks/platform/use-auth'
import {uploadToStorage} from '@/hooks/sgsst/evidencias/use-evidence'
import {validateCedula} from '@/lib/validation'
import type {components} from '@/lib/api-schema'
import type {Page} from '@/lib/page'
import type {SortDir} from '@/hooks/common/use-table-state'

export type Business = components['schemas']['BusinessListItem']
// `logo_url` viene solo con la empresa sola: en la lista sería una URL firmada por fila.
export type BusinessDetail = components['schemas']['BusinessWithRole']
type BusinessUpdate = components['schemas']['BusinessUpdate']

// Vive en `lib/page.ts` para no cerrar un ciclo con `use-evidence`; se re-exporta para sus llamadores.
export type {Page}

export const BUSINESSES_PAGE_SIZE = 10

type BusinessOrigen = components['schemas']['BusinessOrigen']

// The same two filters `core/admin_attention.py` computes server-side.
export type BusinessFiltro = 'evaluacion_en_curso' | 'requiere_atencion'

export type BusinessListParams = {
  page: number
  search: string
  sortBy: string
  sortDir: SortDir
  origen?: BusinessOrigen
  filtro?: BusinessFiltro
}

export function useBusinesses(params: BusinessListParams, enabled = true) {
  return useQuery({
    queryKey: ['businesses', params],
    queryFn: async () =>
      (
        await api.get<Page<Business>>('/businesses', {
          params: {
            page: params.page,
            page_size: BUSINESSES_PAGE_SIZE,
            search: params.search || undefined,
            sort_by: params.sortBy,
            sort_dir: params.sortDir,
            origen: params.origen,
            filtro: params.filtro,
          },
        })
      ).data,
    enabled,
    placeholderData: (previous) => previous,
  })
}

export function useBusiness(businessId: string | undefined) {
  return useQuery({
    queryKey: ['business', businessId],
    queryFn: async () => (await api.get<BusinessDetail>(`/businesses/${businessId}`)).data,
    enabled: !!businessId,
  })
}

// The PATCH returns BusinessRead without `role`, so invalidate instead of seeding; the declared
// worker count also moves the sgsst state.
export function useUpdateBusiness(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    // Partial PATCH (exclude_unset); the contract marks `ai_validate_aportes` required only because it has a default.
    mutationFn: async (input: Partial<BusinessUpdate>) => (await api.patch(`/businesses/${businessId}`, input)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['business', businessId]})
      queryClient.invalidateQueries({queryKey: ['sgsst', businessId]})
      queryClient.invalidateQueries({queryKey: ['businesses']})
    },
  })
}

type LogoUploadTarget = components['schemas']['LogoUploadTargetRead']

export function useUploadBusinessLogo(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({file, onProgress}: {file: File; onProgress: (pct: number) => void}) => {
      const target = (
        await api.post<LogoUploadTarget>(`/businesses/${businessId}/logo-uploads`, {
          filename: file.name,
          content_type: file.type,
          size_bytes: file.size,
        })
      ).data
      await uploadToStorage(target, file, onProgress)
      return (await api.patch(`/businesses/${businessId}`, {logo_object_key: target.object_key})).data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['business', businessId]})
      queryClient.invalidateQueries({queryKey: ['businesses']})
    },
  })
}

export function useRemoveBusinessLogo(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => (await api.patch(`/businesses/${businessId}`, {logo_object_key: null})).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['business', businessId]})
      queryClient.invalidateQueries({queryKey: ['businesses']})
    },
  })
}

export function useIsSystemUser() {
  const {user} = useAuth()
  return user?.is_system_user ?? false
}

export type Capabilities = components['schemas']['CapabilitiesRead']

// Painted as sent, never re-derived. Designing needs the licencia (Res. 0312/2019) and signing (art. 27)
// goes by role, so each control reads its own gate. While loading, everything is closed.
export function useCapabilities(businessId: string | undefined): Capabilities {
  const {data: business} = useBusiness(businessId)
  return (
    business?.capabilities ?? {
      sgsst_operar: false,
      tiene_membresia_real: false,
      sgsst_disenar: false,
      disenar_bloqueado_por: null,
      sgsst_firmar: false,
      firmar_bloqueado_por: null,
      sgsst_dev_override: false,
      responsable_de_area: false,
      sgsst_custodiar_salud: false,
      tiene_sst_verificado: false,
      tiene_responsable_designado: false,
      plan_tier: 'free',
      can_upload_evidence: false,
      can_use_bateria: false,
      can_use_incidentes: false,
      can_execute_plan: false,
      can_manage_employees: false,
      can_use_matriz: false,
      can_use_matriz_legal: false,
      can_use_designaciones: false,
      can_use_jerarquia: false,
      can_use_tags: false,
    }
  )
}

export type AdminUserLookup = components['schemas']['AdminUserLookupRead']

export function useAdminUserLookup(cedula: string) {
  const valid = validateCedula(cedula) === null
  return useQuery({
    queryKey: ['admin-user-lookup', cedula],
    queryFn: async () => (await api.get<AdminUserLookup>('/admin/users/lookup', {params: {cedula}})).data,
    enabled: valid,
  })
}

export function useMyBusinessesPaged(page: number, search: string, enabled = true) {
  return useQuery({
    queryKey: ['businesses', 'mine-paged', page, search],
    queryFn: async () =>
      (
        await api.get<Page<Business>>('/businesses', {
          params: {page, page_size: 6, ...(search ? {search} : {})},
        })
      ).data,
    enabled,
  })
}
